import { queryClient } from '../queryClient';
import { QUERIES } from '../utils/constants';
import { clientLogger } from '../utils/logger';
import { workflowStepMap, workflowTaskMap } from './schema';
import {
  getRetailerSettingsQueryFn,
  type GetRetailerSettingsResult,
} from '../graphql/query/getRetailerSettings';
import type { Workflow } from './schemaTypes';

const fetchRetailerSettings = async () => {
  const { retailerSettings } =
    await queryClient.fetchQuery<GetRetailerSettingsResult>({
      queryKey: [QUERIES.GET_RETAILER_SETTINGS],
      queryFn: getRetailerSettingsQueryFn,
      gcTime: Infinity,
    });
  return retailerSettings;
};

/**
 * These two maps are the only place the app rejects a name it doesn't recognise, so a
 * name the server sends that this build has no entry for has to be dropped here or it
 * is never caught. Spreading the missing entry instead yields a step with no route,
 * and a task that reads as a content block with no rules — so an unknown name is not
 * merely late to fail, it silently becomes a different, valid-looking task.
 *
 * A step whose *name* is unknown is still dropped — there is no route to send a shopper
 * to, so a shorter flow is the only recoverable answer. An unknown **task** is substituted
 * rather than dropped: it has a place on a page that does exist, and deleting it left the
 * step missing an input with nothing to say why. Either way the drift is reported, because
 * nothing else in the stack will say so.
 */
export const getConfig = async () => {
  const retailerSettings = await fetchRetailerSettings();
  const droppedNames: string[] = [];

  const steps = retailerSettings.workflow.flatMap((servedStep) => {
    const step = Object.hasOwn(workflowStepMap, servedStep.id)
      ? workflowStepMap[servedStep.id]
      : undefined;

    if (!step) {
      droppedNames.push(`step:${servedStep.id}`);
      return [];
    }

    const tasks = servedStep.tasks.flatMap((servedTask) => {
      const task = Object.hasOwn(workflowTaskMap, servedTask.id)
        ? workflowTaskMap[servedTask.id]
        : undefined;

      // Substituted, not dropped. Deleting it left the shopper a step missing an input
      // with nothing on the page to say so; the stand-in renders a warning in its place and
      // collects nothing, so it cannot block the step either. The report below is unchanged
      // and is still the only thing that names the id.
      const resolved = task ?? workflowTaskMap.unsupported;

      if (!task) {
        droppedNames.push(`task:${servedStep.id}.${servedTask.id}`);
      }

      return [
        {
          ...resolved,
          guards: servedTask.guards,
          mode: servedTask.mode,
          scope: servedTask.scope,
          conditions: servedTask.conditions,
        },
      ];
    });

    return [{ ...step, tasks }];
  });

  if (droppedNames.length > 0) {
    clientLogger.logError(
      new Error(
        `Served workflow uses names this build does not know: ${droppedNames.join(', ')}`,
      ),
      'Workflow name drift between client and server',
      { droppedNames },
    );
  }

  const config: Workflow = { steps };

  return { config, retailerSettings };
};
